import { useMemo, useState } from "react";

/**
 * FestivalsCalendar · vista calendario mensual del catálogo de festivales.
 *
 * Muestra 3 meses a la vez (anterior, actual, siguiente) para tener
 * contexto de vistas de un vistazo. Cada festival aparece 2 veces si tiene
 * ambas fechas: como "deadline" (pill rojo) en la fecha de inscripción y
 * como "evento" (barra verde) durante el rango event_start→event_end.
 *
 * No renderiza toda la lista al mismo tiempo: filtra por rango visible.
 *
 * Click en un festival → callback onSelect(festival). El padre decide qué
 * hacer (abrir detalle, cambiar tab, etc.).
 */

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAYS_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

function daysInMonth(year, monthZeroIdx) {
  return new Date(year, monthZeroIdx + 1, 0).getDate();
}

// Semanas empiezan en lunes (formato Europa). Devuelve el día de la semana
// (0=lunes, 6=domingo) del primer día del mes.
function startOffset(year, monthZeroIdx) {
  const jsDay = new Date(year, monthZeroIdx, 1).getDay(); // 0=domingo … 6=sábado
  return (jsDay + 6) % 7;
}

function ymd(year, m, d) {
  const mm = String(m + 1).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

function inRange(iso, start, end) {
  if (!iso) return false;
  return iso >= start && iso <= end;
}

// Meses relativos: -1, 0, +1. `base` es el 1er día del mes actual visible.
function offsetMonth(base, delta) {
  return new Date(base.getFullYear(), base.getMonth() + delta, 1);
}

export default function FestivalsCalendar({ festivals, onSelect }) {
  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  // Zoom: 0=compacto, 1=medio, 2=grande. Persistido para respetar la
  // preferencia entre sesiones (calendario se lee mucho, importante que
  // sea legible en el monitor del usuario).
  const [zoom, setZoom] = useState(() => {
    try {
      const raw = parseInt(localStorage.getItem("festivals:calZoom") || "1", 10);
      return Number.isFinite(raw) && raw >= 0 && raw <= 2 ? raw : 1;
    } catch { return 1; }
  });
  const applyZoom = (z) => {
    setZoom(z);
    try { localStorage.setItem("festivals:calZoom", String(z)); } catch (_e) { /* ignore */ }
  };

  const cellStyles = [
    { minH: 62, pad: "p-1", num: "text-[10px]", pill: "text-[9px] py-[1px]", dow: "text-[9px]" },   // compact
    { minH: 100, pad: "p-1.5", num: "text-[12px]", pill: "text-[10px] py-[2px]", dow: "text-[10px]" }, // medium
    { minH: 150, pad: "p-2", num: "text-[14px]", pill: "text-[12px] py-[3px]", dow: "text-[11px]" },   // large
  ];
  const cs = cellStyles[zoom];

  const months = useMemo(() => [
    offsetMonth(cursor, 0),
    offsetMonth(cursor, 1),
    offsetMonth(cursor, 2),
  ], [cursor]);

  // Extrae eventos y deadlines del catálogo, indexados por día ISO.
  const byDay = useMemo(() => {
    const idx = { deadlines: new Map(), events: new Map() };
    for (const f of festivals) {
      for (const ed of (f.editions || [])) {
        if (ed.submission_deadline) {
          const list = idx.deadlines.get(ed.submission_deadline) || [];
          list.push({ festival: f, edition: ed });
          idx.deadlines.set(ed.submission_deadline, list);
        }
        if (ed.event_start) {
          // Repartir el rango event_start → event_end en días.
          const start = new Date(ed.event_start + "T00:00:00Z");
          const end = ed.event_end ? new Date(ed.event_end + "T00:00:00Z") : start;
          const dayMs = 24 * 60 * 60 * 1000;
          for (let d = new Date(start); d <= end; d = new Date(d.getTime() + dayMs)) {
            const iso = d.toISOString().slice(0, 10);
            const list = idx.events.get(iso) || [];
            list.push({ festival: f, edition: ed, isStart: iso === ed.event_start, isEnd: iso === (ed.event_end || ed.event_start) });
            idx.events.set(iso, list);
          }
        }
      }
    }
    return idx;
  }, [festivals]);

  const todayIso = today.toISOString().slice(0, 10);

  return (
    <div className="rounded-xl border border-gb-paper bg-white p-4" data-testid="festivals-calendar">
      {/* Toolbar de navegación */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => setCursor(offsetMonth(cursor, -1))}
                data-testid="festivals-calendar-prev"
                className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:border-[#941620] hover:text-[#941620]">
          ← Anterior
        </button>
        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-stone-500">
          Vista trimestral · {MONTHS_ES[cursor.getMonth()]} {cursor.getFullYear()} → {MONTHS_ES[months[2].getMonth()]} {months[2].getFullYear()}
        </p>
        <div className="flex items-center gap-2">
          {/* Zoom: 3 tamaños de celda */}
          <div className="flex overflow-hidden rounded-full border border-stone-300 bg-white" role="group" aria-label="Zoom del calendario">
            <button onClick={() => applyZoom(0)}
                    data-testid="festivals-calendar-zoom-sm"
                    title="Vista compacta"
                    className={`px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-widest ${zoom === 0 ? "bg-[#941620] text-white" : "text-stone-600 hover:text-[#941620]"}`}>
              🔎−
            </button>
            <button onClick={() => applyZoom(1)}
                    data-testid="festivals-calendar-zoom-md"
                    title="Vista media"
                    className={`px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-widest ${zoom === 1 ? "bg-[#941620] text-white" : "text-stone-600 hover:text-[#941620]"}`}>
              🔎
            </button>
            <button onClick={() => applyZoom(2)}
                    data-testid="festivals-calendar-zoom-lg"
                    title="Vista ampliada"
                    className={`px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-widest ${zoom === 2 ? "bg-[#941620] text-white" : "text-stone-600 hover:text-[#941620]"}`}>
              🔎+
            </button>
          </div>
          <button onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
                  data-testid="festivals-calendar-today"
                  className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:border-[#941620] hover:text-[#941620]">
            Hoy
          </button>
          <button onClick={() => setCursor(offsetMonth(cursor, 1))}
                  data-testid="festivals-calendar-next"
                  className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:border-[#941620] hover:text-[#941620]">
            Siguiente →
          </button>
        </div>
      </div>

      {/* Leyenda */}
      <div className="mb-3 flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-widest text-stone-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#941620]"></span>
          Deadline inscripción
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded bg-[#26775E]"></span>
          Evento
        </span>
        <span className="flex items-center gap-1.5">⭐ Me interesa</span>
        <span className="flex items-center gap-1.5">✉️ Inscritos</span>
        <span className="flex items-center gap-1.5">🎟️ Vamos</span>
        <span className="ml-auto text-[10px] text-stone-400 normal-case tracking-normal">Click en un evento para ver detalle, notas y editar.</span>
      </div>

      <div className={`grid grid-cols-1 gap-6 ${zoom === 2 ? "" : zoom === 1 ? "lg:grid-cols-2 2xl:grid-cols-3" : "lg:grid-cols-3"}`}>
        {months.map((m) => (
          <MonthGrid
            key={`${m.getFullYear()}-${m.getMonth()}`}
            year={m.getFullYear()}
            monthZeroIdx={m.getMonth()}
            byDay={byDay}
            onSelect={onSelect}
            todayIso={todayIso}
            cs={cs}
          />
        ))}
      </div>
    </div>
  );
}

function MonthGrid({ year, monthZeroIdx, byDay, onSelect, todayIso, cs }) {
  const totalDays = daysInMonth(year, monthZeroIdx);
  const startPad = startOffset(year, monthZeroIdx);
  const monthLabel = MONTHS_ES[monthZeroIdx];

  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  // Cuántos eventos caben visibles por celda según el zoom.
  const maxPerType = cs.minH >= 130 ? 5 : cs.minH >= 90 ? 3 : 2;

  const iconsFor = (festival) => {
    const statuses = festival.project_statuses || [];
    const parts = [];
    const anySubmitted = statuses.some(s => s?.submitted);
    // "Vamos" combina el flag de estudio (asistimos como oyentes) con
    // el flag por-proyecto (asistimos con esa peli). Basta con uno.
    const anyAttending = festival.studio_attending || statuses.some(s => s?.attending);
    if (anySubmitted) parts.push("✉️");
    if (anyAttending) parts.push("🎟️");
    if (festival.studio_interested) parts.push("⭐");
    return parts.join("");
  };

  return (
    <div>
      <h3 className="mb-2 font-serif text-lg text-stone-900">
        {monthLabel} <span className="text-stone-400">{year}</span>
      </h3>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border border-stone-200 bg-stone-200 text-[11px]">
        {WEEKDAYS_SHORT.map(w => (
          <div key={w} className={`bg-stone-100 px-1 py-1 text-center font-semibold uppercase tracking-widest text-stone-500 ${cs.dow}`}>{w}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={i} style={{ minHeight: cs.minH }} className="bg-stone-50/60"></div>;
          const iso = ymd(year, monthZeroIdx, d);
          const isToday = iso === todayIso;
          const deadlines = byDay.deadlines.get(iso) || [];
          const events = byDay.events.get(iso) || [];
          return (
            <div key={i} style={{ minHeight: cs.minH }} className={`${cs.pad} ${isToday ? "bg-amber-50" : "bg-white"}`}>
              <div className={`mb-0.5 font-semibold ${cs.num} ${isToday ? "text-amber-800" : "text-stone-500"}`}>
                {d}
              </div>
              <div className="flex flex-col gap-0.5">
                {deadlines.slice(0, maxPerType).map((x, k) => (
                  <button key={`d-${k}`}
                          onClick={() => onSelect?.(x.festival)}
                          title={`Deadline: ${x.festival.name}`}
                          data-testid={`cal-deadline-${x.festival.slug}`}
                          className={`truncate rounded-sm bg-[#941620]/15 px-1 text-left leading-tight text-[#941620] hover:bg-[#941620]/25 ${cs.pill}`}>
                    <span className="mr-0.5">●</span>
                    {iconsFor(x.festival)}
                    <span className="ml-0.5">{x.festival.name}</span>
                  </button>
                ))}
                {events.slice(0, maxPerType).map((x, k) => (
                  <button key={`e-${k}`}
                          onClick={() => onSelect?.(x.festival)}
                          title={`Evento: ${x.festival.name}`}
                          data-testid={`cal-event-${x.festival.slug}`}
                          className={`truncate px-1 text-left leading-tight text-white ${x.isStart ? "rounded-l-sm" : ""} ${x.isEnd ? "rounded-r-sm" : ""} bg-[#26775E] hover:bg-[#1f5d48] ${cs.pill}`}>
                    {iconsFor(x.festival)}
                    <span className="ml-0.5">{x.festival.name}</span>
                  </button>
                ))}
                {(deadlines.length + events.length) > (maxPerType * 2) && (
                  <span className="text-[9px] text-stone-400">+{deadlines.length + events.length - (maxPerType * 2)} más</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
